import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Title, useRedirect } from 'react-admin';
import { CustomSnackbar } from '../../common/CustomSnackbar';
import { PageHeader } from '../../common/PageHeader/PageHeader';
import { DocumentSource, DocumentType } from '../../../interfaces/document';
import referralService from '../../../services/referral';
import { BulkReprocessPreviewTable } from './BulkReprocessPreviewTable';
import { DocumentTypeMultiSelect } from './DocumentTypeMultiSelect';
import { useBulkReprocess } from './useBulkReprocess';

const DEFAULT_SPACING_MS = 30000;

const BulkReprocess: React.FC = () => {
  const redirect = useRedirect();
  const {
    preview,
    executeResult,
    loadingPreview,
    loadingExecute,
    snackbar,
    runPreview,
    runExecute,
    closeSnackbar,
  } = useBulkReprocess();

  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [createdAtFrom, setCreatedAtFrom] = useState('');
  const [createdAtTo, setCreatedAtTo] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [source, setSource] = useState<string>('');
  const [receivingClinicId, setReceivingClinicId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [spacingMs, setSpacingMs] = useState<number>(DEFAULT_SPACING_MS);

  useEffect(() => {
    referralService.fetchDocumentTypes().then((response) => {
      if (response?.success) {
        setDocumentTypes(response.documentContents);
      }
    });
  }, []);

  const canPreview = useMemo(
    () =>
      Boolean(createdAtFrom) &&
      Boolean(createdAtTo) &&
      selectedTypes.length > 0 &&
      !loadingPreview,
    [createdAtFrom, createdAtTo, selectedTypes, loadingPreview],
  );

  const canConfirm = useMemo(() => {
    if (!preview || preview.count === 0) return false;
    if (preview.count > preview.maxBatchSize) return false;
    if (reason.trim().length < 5) return false;
    if (loadingExecute) return false;
    return true;
  }, [preview, reason, loadingExecute]);

  const handlePreview = () => {
    runPreview({
      createdAtFrom: new Date(createdAtFrom).toISOString(),
      createdAtTo: new Date(createdAtTo).toISOString(),
      currentDocumentTypes: selectedTypes,
      source: source || undefined,
      receivingClinicId: receivingClinicId ? Number(receivingClinicId) : undefined,
    });
  };

  const handleExecute = () => {
    if (!canConfirm) return;
    if (
      !window.confirm(
        `Re-process ${preview!.count} document(s)? This will discard their current classifications and re-enqueue them with ~${Math.round(spacingMs / 1000)}s spacing.`,
      )
    ) {
      return;
    }
    runExecute({ reason: reason.trim(), spacingMs });
  };

  const handleCopyBatchId = () => {
    if (executeResult) {
      navigator.clipboard.writeText(executeResult.batchId);
    }
  };

  return (
    <Box mt={4}>
      <Title title='Bulk Document Reprocess' />
      <PageHeader
        breadcrumbs={[
          { label: 'Documents', onClick: () => redirect('/documents/library') },
          { label: 'Bulk Reprocess', active: true },
        ]}
      />

      <Alert severity='info' sx={{ mb: 2 }}>
        Re-runs classification + extraction on documents matching the filter. Manually-overridden
        detected documents are preserved. Jobs are staggered to avoid disrupting live ingestion.
      </Alert>

      <Paper variant='outlined' sx={{ p: 2 }}>
        <Typography variant='subtitle1' sx={{ mb: 2 }}>
          Filter
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size='small'
              type='datetime-local'
              label='Created from'
              InputLabelProps={{ shrink: true }}
              value={createdAtFrom}
              onChange={(e) => setCreatedAtFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size='small'
              type='datetime-local'
              label='Created to'
              InputLabelProps={{ shrink: true }}
              value={createdAtTo}
              onChange={(e) => setCreatedAtTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DocumentTypeMultiSelect
              options={documentTypes.map((dt) => dt.name)}
              value={selectedTypes}
              onChange={setSelectedTypes}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              size='small'
              fullWidth
              options={Object.values(DocumentSource)}
              value={source || null}
              onChange={(_, newValue) => setSource(newValue ?? '')}
              renderInput={(params) => (
                <TextField {...params} label='Source (optional)' size='small' />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size='small'
              type='number'
              label='Receiving clinic ID (optional)'
              value={receivingClinicId}
              onChange={(e) => setReceivingClinicId(e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant='contained' onClick={handlePreview} disabled={!canPreview}>
            {loadingPreview ? 'Previewing…' : 'Preview'}
          </Button>
        </Box>
      </Paper>

      {preview && <BulkReprocessPreviewTable preview={preview} />}

      {preview && preview.count > 0 && (
        <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
          <Typography variant='subtitle1' sx={{ mb: 2 }}>
            Confirm reprocess
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size='small'
                label='Reason (recorded in audit trail)'
                placeholder='e.g. Bedrock misclassification recovery 2026-05'
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                helperText='Minimum 5 characters'
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size='small'
                type='number'
                label='Spacing (ms)'
                value={spacingMs}
                inputProps={{ min: 5000, max: 300000, step: 1000 }}
                onChange={(e) => setSpacingMs(Number(e.target.value))}
                helperText='Delay between consecutive jobs'
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant='contained'
              color='warning'
              onClick={handleExecute}
              disabled={!canConfirm}>
              {loadingExecute ? 'Enqueuing…' : `Reprocess ${preview.count} document(s)`}
            </Button>
          </Box>
        </Paper>
      )}

      {executeResult && (
        <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
          <Typography variant='subtitle1' sx={{ mb: 1 }}>
            Batch enqueued
          </Typography>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Typography variant='body2'>
              <strong>Batch ID:</strong>{' '}
              <code style={{ fontFamily: 'monospace' }}>{executeResult.batchId}</code>
            </Typography>
            <Tooltip title='Copy batch ID'>
              <IconButton size='small' onClick={handleCopyBatchId}>
                <ContentCopyIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant='body2' sx={{ mt: 1 }}>
            Enqueued: <strong>{executeResult.enqueuedCount}</strong>
            {'  ·  '}
            Failed: <strong>{executeResult.failedIds.length}</strong>
            {'  ·  '}
            Spacing: <strong>{executeResult.spacingMs} ms</strong>
          </Typography>
          {executeResult.failedIds.length > 0 && (
            <Alert severity='warning' sx={{ mt: 2 }}>
              Failed IDs: {executeResult.failedIds.join(', ')}
            </Alert>
          )}
        </Paper>
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
    </Box>
  );
};

export default BulkReprocess;
